// src/components/defined/budget_quantities/QuantitiesBoard.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   QUANTITIES BOARD — CORELIA
   ----------------------------------------------------------------------------
   ZONE C1
   - connecté au banner via activeFilter / onBannerDataChange
   - sélecteur de package conservé
   - tableau principal sans scroll horizontal
   - hauteur forcée pour afficher confortablement ~8 lignes
   - zone basse coefficients / règles empiriques
   - aucune dette technique / structure simple et robuste
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  panel: theme?.colors?.background?.panel || "#f1f3f6",
  border: theme?.colors?.borders?.default || "#d6dee4",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

const DRIVER_OPTIONS = ["Manual", "Derived", "From parent", "Fixed"];
const FILTER_OPTIONS = ["Quantities", "Drivers", "Rules"];

const PACKAGE_OPTIONS = [
  "2.1 Transmission Main",
  "2.2 Secondary Branch",
  "2.3 Service Connections",
  "3.1 Trench Excavation",
  "3.2 Pipe Bedding",
  "3.3 Backfilling",
  "4.1 Valve Chambers",
  "4.2 Ancillary Equipment",
];

const INITIAL_ROWS = [
  {
    id: "r1",
    code: "1.1",
    packageRef: "2.1 Transmission Main",
    article: "Supply HDPE Pipe DN400 PN16",
    type: "Material",
    unit: "m",
    driver: "Manual",
    quantity: "1200",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "",
    note: "",
  },
  {
    id: "r2",
    code: "1.2",
    packageRef: "2.1 Transmission Main",
    article: "Installation HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    driver: "From parent",
    quantity: "1200",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Lot A",
    note: "",
  },
  {
    id: "r3",
    code: "1.3",
    packageRef: "2.1 Transmission Main",
    article: "Jointing HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    driver: "Derived",
    quantity: "1200",
    source: "Rule",
    factor: "1.00",
    ratio: "Pipe length",
    batch: "Lot A",
    note: "Field ratio",
  },
  {
    id: "r4",
    code: "1.4",
    packageRef: "2.1 Transmission Main",
    article: "Transport and handling DN400",
    type: "Logistics",
    unit: "m",
    driver: "Derived",
    quantity: "1260",
    source: "Rule",
    factor: "1.05",
    ratio: "Pipe length",
    batch: "Lot A",
    note: "+5%",
  },
  {
    id: "r5",
    code: "1.5",
    packageRef: "2.1 Transmission Main",
    article: "Pipe stringing supervision",
    type: "Labor",
    unit: "m",
    driver: "Fixed",
    quantity: "1",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "",
    note: "",
  },
  {
    id: "r6",
    code: "2.1",
    packageRef: "2.2 Secondary Branch",
    article: "Supply HDPE Pipe DN250",
    type: "Material",
    unit: "m",
    driver: "Manual",
    quantity: "850",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Lot B",
    note: "",
  },
  {
    id: "r7",
    code: "2.2",
    packageRef: "2.2 Secondary Branch",
    article: "Installation HDPE Pipe DN250",
    type: "Labor",
    unit: "m",
    driver: "From parent",
    quantity: "850",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Lot B",
    note: "",
  },
  {
    id: "r8",
    code: "2.3",
    packageRef: "2.2 Secondary Branch",
    article: "Transport and handling DN250",
    type: "Logistics",
    unit: "m",
    driver: "Derived",
    quantity: "876",
    source: "Rule",
    factor: "1.03",
    ratio: "Pipe length",
    batch: "Lot B",
    note: "+3%",
  },
  {
    id: "r9",
    code: "3.1",
    packageRef: "2.3 Service Connections",
    article: "Service pipe DN63",
    type: "Material",
    unit: "m",
    driver: "Manual",
    quantity: "4600",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Lot C",
    note: "",
  },
  {
    id: "r10",
    code: "3.2",
    packageRef: "2.3 Service Connections",
    article: "Fittings and accessories DN63",
    type: "Equipment",
    unit: "unit",
    driver: "Derived",
    quantity: "380",
    source: "Rule",
    factor: "1.00",
    ratio: "Connection count",
    batch: "Lot C",
    note: "",
  },
  {
    id: "r11",
    code: "3.3",
    packageRef: "2.3 Service Connections",
    article: "Connection installation crews",
    type: "Labor",
    unit: "unit",
    driver: "From parent",
    quantity: "380",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Lot C",
    note: "",
  },
  {
    id: "r12",
    code: "4.1",
    packageRef: "3.1 Trench Excavation",
    article: "Excavation in common soil",
    type: "Subcontract",
    unit: "m³",
    driver: "Manual",
    quantity: "1850",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Civil 1",
    note: "",
  },
  {
    id: "r13",
    code: "4.2",
    packageRef: "3.1 Trench Excavation",
    article: "Spoil evacuation",
    type: "Logistics",
    unit: "m³",
    driver: "Derived",
    quantity: "1850",
    source: "Rule",
    factor: "1.00",
    ratio: "Excavation volume",
    batch: "Civil 1",
    note: "",
  },
  {
    id: "r14",
    code: "4.3",
    packageRef: "3.1 Trench Excavation",
    article: "Excavation in rock pockets",
    type: "Subcontract",
    unit: "m³",
    driver: "Manual",
    quantity: "95",
    source: "Imported",
    factor: "1.15",
    ratio: "",
    batch: "Civil 1",
    note: "Geotech prelim.",
  },
  {
    id: "r15",
    code: "5.1",
    packageRef: "3.2 Pipe Bedding",
    article: "Sand bedding layer",
    type: "Material",
    unit: "m³",
    driver: "Manual",
    quantity: "420",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Civil 2",
    note: "",
  },
  {
    id: "r16",
    code: "5.2",
    packageRef: "3.2 Pipe Bedding",
    article: "Bedding placement",
    type: "Labor",
    unit: "m³",
    driver: "From parent",
    quantity: "420",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Civil 2",
    note: "",
  },
  {
    id: "r17",
    code: "6.1",
    packageRef: "3.3 Backfilling",
    article: "Selected backfill",
    type: "Material",
    unit: "m³",
    driver: "Manual",
    quantity: "1320",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Civil 3",
    note: "",
  },
  {
    id: "r18",
    code: "6.2",
    packageRef: "3.3 Backfilling",
    article: "Compaction works",
    type: "Subcontract",
    unit: "m³",
    driver: "From parent",
    quantity: "1320",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Civil 3",
    note: "",
  },
  {
    id: "r19",
    code: "7.1",
    packageRef: "4.1 Valve Chambers",
    article: "Butterfly valves DN400",
    type: "Equipment",
    unit: "unit",
    driver: "Manual",
    quantity: "4",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Valve A",
    note: "",
  },
  {
    id: "r20",
    code: "7.2",
    packageRef: "4.1 Valve Chambers",
    article: "Valve chamber civil works",
    type: "Subcontract",
    unit: "unit",
    driver: "From parent",
    quantity: "4",
    source: "Inherited",
    factor: "1.00",
    ratio: "",
    batch: "Valve A",
    note: "",
  },
  {
    id: "r21",
    code: "8.1",
    packageRef: "4.2 Ancillary Equipment",
    article: "Air release valves",
    type: "Equipment",
    unit: "unit",
    driver: "Manual",
    quantity: "12",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Valve B",
    note: "",
  },
  {
    id: "r22",
    code: "8.2",
    packageRef: "4.2 Ancillary Equipment",
    article: "Washout valves",
    type: "Equipment",
    unit: "unit",
    driver: "Manual",
    quantity: "6",
    source: "Manual",
    factor: "1.00",
    ratio: "",
    batch: "Valve B",
    note: "",
  },
];

const INITIAL_RULES = [
  {
    id: "c1",
    scope: "Selected rows",
    action: "Multiply",
    target: "Factor",
    value: "1.05",
    batch: "",
  },
];

const RULE_SCOPE_OPTIONS = ["Selected rows", "Current package", "Batch"];
const RULE_ACTION_OPTIONS = ["Multiply", "Add", "Replace", "Set note"];
const RULE_TARGET_OPTIONS = ["Factor", "Batch", "Note"];

const S = {
  page: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 348px) minmax(0, 1fr)",
    gap: 10,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  topBar: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "auto 260px 1fr",
    alignItems: "start",
    gap: 10,
    padding: "2px 0 0 0",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 3,
  },

  topLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.soft,
    whiteSpace: "nowrap",
    paddingTop: 6,
  },

  select: {
    width: "100%",
    height: 26,
    minWidth: 0,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: T.bg,
    padding: "0 8px",
    boxSizing: "border-box",
    outline: "none",
    color: T.text,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
  },

  multiSelectWrap: {
    width: "100%",
    minWidth: 0,
    position: "relative",
  },

  multiSelectButton: {
    width: "100%",
    height: 26,
    minWidth: 0,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: T.bg,
    padding: "0 28px 0 8px",
    boxSizing: "border-box",
    outline: "none",
    color: T.text,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    textAlign: "left",
    cursor: "pointer",
    position: "relative",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  multiSelectCaret: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    color: T.verySoft,
    pointerEvents: "none",
    fontSize: "0.62rem",
    lineHeight: 1,
  },

  multiSelectMenu: {
    position: "absolute",
    top: 30,
    left: 0,
    right: 0,
    background: T.bg,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    boxShadow: "0 6px 18px rgba(15, 41, 53, 0.08)",
    padding: "6px 0",
    boxSizing: "border-box",
    maxHeight: 220,
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 5,
  },

  multiSelectAction: {
    width: "100%",
    height: 24,
    border: "none",
    background: "transparent",
    color: T.corelia,
    cursor: "pointer",
    textAlign: "left",
    padding: "0 10px",
    boxSizing: "border-box",
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.70rem",
    fontWeight: 500,
  },

  multiSelectDivider: {
    width: "100%",
    height: 1,
    background: "rgba(214,222,228,0.70)",
    margin: "4px 0",
  },

  multiSelectItem: {
    width: "100%",
    minHeight: 24,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 10px",
    boxSizing: "border-box",
    cursor: "pointer",
  },

  multiSelectItemLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  tableShell: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr)",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  tableHead: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns:
      "34px 64px minmax(240px,1.9fr) 28px 86px 74px 92px 86px 74px 74px 90px minmax(160px,1fr)",
    gap: 8,
    alignItems: "center",
    padding: "0 6px 6px 6px",
    borderBottom: `1px solid rgba(214,222,228,0.70)`,
    boxSizing: "border-box",
  },

  headText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.69rem",
    lineHeight: 1,
    color: T.verySoft,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    minWidth: 0,
  },

  tableBody: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    scrollbarGutter: "stable",
  },

  row: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns:
      "34px 64px minmax(240px,1.9fr) 28px 86px 74px 92px 86px 74px 74px 90px minmax(160px,1fr)",
    gap: 8,
    alignItems: "center",
    padding: "1px 6px",
    minHeight: 32,
    borderBottom: `1px solid rgba(214,222,228,0.50)`,
    boxSizing: "border-box",
  },

  articleCell: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  articleTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.12,
    color: T.text,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  articleMeta: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.68rem",
    lineHeight: 1.08,
    color: T.verySoft,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  rowText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.soft,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  sourceBadge: {
    height: 20,
    minWidth: 58,
    padding: "0 6px",
    borderRadius: theme?.borders?.radius?.md || "4px",
    border: `1px solid rgba(14,63,86,0.12)`,
    background: "rgba(14,63,86,0.04)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.68rem",
    color: T.corelia,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  checkboxWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  checkbox: {
    width: 13,
    height: 13,
    accentColor: T.corelia,
    cursor: "pointer",
  },

  statusWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
  },

  input: {
    width: "100%",
    minWidth: 0,
    height: 22,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: T.bg,
    padding: "0 6px",
    boxSizing: "border-box",
    outline: "none",
    color: T.text,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
  },

  noteInput: {
    width: "100%",
    minWidth: 0,
    height: 22,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: "#ffffff",
    padding: "0 6px",
    boxSizing: "border-box",
    outline: "none",
    color: T.text,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
  },

  coeffShell: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto auto minmax(0, 1fr)",
    overflow: "hidden",
    boxSizing: "border-box",
    paddingTop: 4,
  },

  coeffHeader: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 0 8px 0",
    boxSizing: "border-box",
  },

  plusButton: {
    width: 22,
    height: 22,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: T.bg,
    color: T.corelia,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.80rem",
    lineHeight: 1,
  },

  coeffTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.1,
    color: T.corelia,
    fontWeight: 600,
  },

  coeffHead: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "54px 120px 120px 120px minmax(160px,1fr) 88px",
    gap: 8,
    alignItems: "center",
    padding: "0 0 6px 0",
    borderBottom: `1px solid rgba(214,222,228,0.70)`,
    boxSizing: "border-box",
  },

  coeffBody: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
  },

  coeffRow: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "54px 120px 120px 120px minmax(160px,1fr) 88px",
    gap: 8,
    alignItems: "center",
    padding: "3px 0",
    boxSizing: "border-box",
  },

  coeffLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.soft,
    whiteSpace: "nowrap",
  },

  applyButton: {
    height: 22,
    border: `1px solid ${T.border}`,
    borderRadius: theme?.borders?.radius?.md || "4px",
    background: T.bg,
    color: T.corelia,
    cursor: "pointer",
    boxSizing: "border-box",
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
  },
};

const getSourceFromDriver = (driver) => {
  if (driver === "Derived") return "Rule";
  if (driver === "From parent") return "Inherited";
  return "Manual";
};

const getStatusColor = (source) => {
  if (source === "Manual") return T.green;
  if (source === "Inherited") return T.corelia;
  if (source === "Rule") return T.caneva;
  return T.redText;
};

const getBannerMetrics = (rows) => {
  const lineCount = rows.length;
  const definedCount = rows.filter((row) => `${row.quantity}`.trim() !== "").length;
  const ruleCount = rows.filter((row) => row.driver === "Derived").length;

  return [
    { label: "Lines", value: lineCount, valueColor: T.corelia },
    { label: "Defined", value: definedCount, valueColor: T.greenText },
    { label: "Rule-based", value: ruleCount, valueColor: T.orangeText },
  ];
};

const getBannerLegends = () => [
  { label: "Manual", color: T.corelia },
  { label: "Inherited", color: T.green },
  { label: "Rule", color: T.caneva },
];

const QuantitiesBoard = ({
  activeFilter = "Quantities",
  onBannerDataChange,
}) => {
  const [selectedPackages, setSelectedPackages] = useState([PACKAGE_OPTIONS[0]]);
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [rules, setRules] = useState(INITIAL_RULES);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isPackageMenuOpen, setIsPackageMenuOpen] = useState(false);
  const packageMenuRef = useRef(null);

  const visibleRows = useMemo(() => {
    let baseRows = rows.filter((row) => selectedPackages.includes(row.packageRef));

    if (activeFilter === "Drivers") {
      baseRows = baseRows.filter((row) => row.driver === "Derived" || row.driver === "From parent");
    }

    if (activeFilter === "Rules") {
      baseRows = baseRows.filter((row) => row.driver === "Derived");
    }

    return baseRows;
  }, [rows, selectedPackages, activeFilter]);

  const bannerMetrics = useMemo(() => getBannerMetrics(visibleRows), [visibleRows]);
  const bannerLegends = useMemo(() => getBannerLegends(), []);

  useEffect(() => {
    if (typeof onBannerDataChange !== "function") return;

    onBannerDataChange({
      metrics: bannerMetrics,
      legends: bannerLegends,
      filterOptions: FILTER_OPTIONS,
      showMetrics: true,
      showFilters: true,
      showLegends: true,
    });
  }, [bannerMetrics, bannerLegends, onBannerDataChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!packageMenuRef.current) return;
      if (!packageMenuRef.current.contains(event.target)) {
        setIsPackageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleRowChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const nextRow = { ...row, [field]: value };

        if (field === "driver") {
          nextRow.source = getSourceFromDriver(value);
        }

        return nextRow;
      })
    );
  };

  const handleRuleChange = (id, field, value) => {
    setRules((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, [field]: value } : rule))
    );
  };

  const handleAddRule = () => {
    setRules((prev) => [
      ...prev,
      {
        id: `c${prev.length + 1}`,
        scope: "Selected rows",
        action: "Multiply",
        target: "Factor",
        value: "",
        batch: "",
      },
    ]);
  };

  const handleApplyRule = (ruleId) => {
    const rule = rules.find((item) => item.id === ruleId);
    if (!rule) return;

    setRows((prev) =>
      prev.map((row) => {
        const inPackage = selectedPackages.includes(row.packageRef);
        if (!inPackage) return row;

        const matchSelected =
          rule.scope === "Selected rows" ? selectedRowIds.includes(row.id) : false;
        const matchPackage = rule.scope === "Current package";
        const matchBatch = rule.scope === "Batch" && rule.batch && row.batch === rule.batch;

        if (!(matchSelected || matchPackage || matchBatch)) return row;

        if (rule.target === "Factor") {
          if (rule.action === "Multiply" && rule.value) {
            const base = parseFloat(row.factor || "1");
            const factor = parseFloat(rule.value || "1");
            if (!Number.isNaN(base) && !Number.isNaN(factor)) {
              return { ...row, factor: (base * factor).toFixed(2) };
            }
          }

          if (rule.action === "Replace") {
            return { ...row, factor: rule.value };
          }
        }

        if (rule.target === "Batch" && rule.action === "Replace") {
          return { ...row, batch: rule.value };
        }

        if (rule.target === "Note" && rule.action === "Set note") {
          return { ...row, note: rule.value };
        }

        return row;
      })
    );
  };

  const packageButtonLabel =
    selectedPackages.length === 0
      ? "No package selected"
      : selectedPackages.length === PACKAGE_OPTIONS.length
      ? "All packages"
      : selectedPackages.join(", ");

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div style={S.topLabel}>Package</div>

        <div style={S.multiSelectWrap} ref={packageMenuRef}>
          <button
            type="button"
            style={S.multiSelectButton}
            onClick={() => setIsPackageMenuOpen((prev) => !prev)}
          >
            {packageButtonLabel}
            <span style={S.multiSelectCaret}>▼</span>
          </button>

          {isPackageMenuOpen && (
            <div style={S.multiSelectMenu}>
              <button
                type="button"
                style={S.multiSelectAction}
                onClick={() => setSelectedPackages(PACKAGE_OPTIONS)}
              >
                Select All
              </button>

              <button
                type="button"
                style={S.multiSelectAction}
                onClick={() => setSelectedPackages([])}
              >
                Deselect All
              </button>

              <div style={S.multiSelectDivider} />

              {PACKAGE_OPTIONS.map((option) => (
                <label key={option} style={S.multiSelectItem}>
                  <input
                    type="checkbox"
                    checked={selectedPackages.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPackages((prev) => [...prev, option]);
                      } else {
                        setSelectedPackages((prev) => prev.filter((item) => item !== option));
                      }
                    }}
                    style={S.checkbox}
                  />
                  <span style={S.multiSelectItemLabel}>{option}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div />
      </div>

      <div style={S.tableShell}>
        <div style={S.tableHead}>
          <div style={S.headText}></div>
          <div style={S.headText}>Code</div>
          <div style={S.headText}>Article</div>
          <div style={S.headText}></div>
          <div style={S.headText}>Type</div>
          <div style={S.headText}>Unit</div>
          <div style={S.headText}>Driver</div>
          <div style={S.headText}>Quantity</div>
          <div style={S.headText}>Source</div>
          <div style={S.headText}>Factor</div>
          <div style={S.headText}>Base / ratio</div>
          <div style={S.headText}>Note / observation</div>
        </div>

        <div style={S.tableBody}>
          {visibleRows.map((row) => (
            <div key={row.id} style={S.row}>
              <div style={S.checkboxWrap}>
                <input
                  type="checkbox"
                  checked={selectedRowIds.includes(row.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRowIds((prev) => [...prev, row.id]);
                    } else {
                      setSelectedRowIds((prev) => prev.filter((item) => item !== row.id));
                    }
                  }}
                  style={S.checkbox}
                />
              </div>

              <div style={S.rowText}>{row.code}</div>

              <div style={S.articleCell}>
                <div style={S.articleTitle}>{row.article}</div>
                <div style={S.articleMeta}>{row.packageRef}</div>
              </div>

              <div style={S.statusWrap}>
                <span
                  style={{
                    ...S.statusDot,
                    background: getStatusColor(row.source),
                  }}
                />
              </div>

              <div style={S.rowText}>{row.type}</div>
              <div style={S.rowText}>{row.unit}</div>

              <select
                value={row.driver}
                onChange={(e) => handleRowChange(row.id, "driver", e.target.value)}
                style={S.select}
              >
                {DRIVER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={row.quantity}
                onChange={(e) => handleRowChange(row.id, "quantity", e.target.value)}
                style={S.input}
              />

              <div style={S.sourceBadge}>{row.source}</div>

              <input
                type="text"
                value={row.factor}
                onChange={(e) => handleRowChange(row.id, "factor", e.target.value)}
                style={S.input}
              />

              <input
                type="text"
                value={row.ratio}
                onChange={(e) => handleRowChange(row.id, "ratio", e.target.value)}
                style={S.input}
              />

              <input
                type="text"
                value={row.note}
                onChange={(e) => handleRowChange(row.id, "note", e.target.value)}
                style={S.noteInput}
              />
            </div>
          ))}
        </div>
      </div>

      <div style={S.coeffShell}>
        <div style={S.coeffHeader}>
          <button type="button" style={S.plusButton} onClick={handleAddRule}>
            +
          </button>
          <div style={S.coeffTitle}>Empirical coefficients</div>
        </div>

        <div style={S.coeffHead}>
          <div style={S.headText}>Coef.</div>
          <div style={S.headText}>Scope</div>
          <div style={S.headText}>Action</div>
          <div style={S.headText}>Target</div>
          <div style={S.headText}>Value</div>
          <div style={S.headText}>Apply</div>
        </div>

        <div style={S.coeffBody}>
          {rules.map((rule, index) => (
            <div key={rule.id} style={S.coeffRow}>
              <div style={S.coeffLabel}>{`Coef. ${index + 1}`}</div>

              <select
                value={rule.scope}
                onChange={(e) => handleRuleChange(rule.id, "scope", e.target.value)}
                style={S.select}
              >
                {RULE_SCOPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <select
                value={rule.action}
                onChange={(e) => handleRuleChange(rule.id, "action", e.target.value)}
                style={S.select}
              >
                {RULE_ACTION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <select
                value={rule.target}
                onChange={(e) => handleRuleChange(rule.id, "target", e.target.value)}
                style={S.select}
              >
                {RULE_TARGET_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={rule.target === "Batch" ? rule.batch : rule.value}
                onChange={(e) => {
                  if (rule.target === "Batch") {
                    handleRuleChange(rule.id, "batch", e.target.value);
                  } else {
                    handleRuleChange(rule.id, "value", e.target.value);
                  }
                }}
                style={S.input}
              />

              <button
                type="button"
                style={S.applyButton}
                onClick={() => handleApplyRule(rule.id)}
              >
                Apply
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuantitiesBoard;